import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { formatPlainTextExcerpt } from '../utils/textFormatting.jsx';

const InsightCard = ({ 
  insight, 
  onMouseEnter, 
  onMouseLeave, 
  onClick,
  className = '',
  style = {}
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const handleClick = () => {
    if (onClick) {
      onClick(insight);
    } else {
      // Use ID-based navigation for guaranteed uniqueness
      const insightId = insight.id;
      const url = `/${currentLanguage}/insights/id/${insightId}`
      console.log('🔗 InsightCard direct navigation:', {
        name: insight.Name,
        id: insightId,
        targetUrl: url
      });
      navigate(url);
      
      // Scroll to top after navigation
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100);
    }
  };

  // Get title based on current language
  const getTitle = () => {
    return insight[`title_${currentLanguage}`] || insight.title_en || insight.title_ar || insight.title || insight.Name || ''
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLanguage === 'ar' ? 'ar-SA' : 'en-GB', {
      day: '2-digit',
      month: currentLanguage === 'ar' ? 'long' : '2-digit',
      year: 'numeric'
    });
  };

  const parseTags = (tagsString) => {
    if (!tagsString) return [];
    try {
      return JSON.parse(tagsString);
    } catch {
      return [];
    }
  };

  const _tags = parseTags(insight.tags);

  return (
    <article
      className={`masonry-cards_item promoted-item card card--background-image ${className}`}
      style={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        boxSizing: "border-box",
        minHeight: "300px",
        height: "100%",
        margin: "0px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#000000",
        backgroundImage: `url('${insight.image || insight.background_image || insight.featured_image || '/pic/1.jpg'}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        cursor: "pointer",
        ...style
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={handleClick}
    >
      {/* Dark overlay for text readability */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.7))',
        zIndex: 1
      }} />

      {/* Title that moves to top on hover */}
      <div 
        className="card-title"
        style={{
          position: 'absolute',
          bottom: '16px',
          left: '16px',
          right: '16px',
          zIndex: 3,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: 'translateY(0)',
          padding: '0 4px',
          boxSizing: 'border-box'
        }}
      >
        {/* Title */}
        <h3 style={{
          margin: "0px 0px 8px 0px",
          fontFamily: "Inter, sans-serif",
          fontSize: "clamp(14px, 2.5vw, 18px)",
          fontWeight: 700,
          lineHeight: "1.3em",
          color: "white",
          letterSpacing: "-0.02em",
          textShadow: "0 2px 8px rgba(0, 0, 0, 0.8)",
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          maxWidth: '100%',
          wordWrap: 'break-word',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          {getTitle()}
        </h3>

        {/* Date and Category - Same Level Tags */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Date */}
          <div style={{
            padding: '6px 12px',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(10px)',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            <p style={{
              margin: "0px",
              fontFamily: "Inter, sans-serif",
              fontSize: "9px",
              fontWeight: 800,
              color: "rgba(255, 255, 255, 0.95)",
              textTransform: "uppercase",
              letterSpacing: "0.15em",
              transition: 'opacity 0.3s ease',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {formatDate(insight.published_at || insight.date || insight.Date)}
            </p>
          </div>

          {/* Category */}
          <div style={{
            padding: '6px 12px',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(10px)',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            <p style={{
              margin: "0px",
              fontFamily: "Inter, sans-serif",
              fontSize: "9px",
              fontWeight: 800,
              color: "rgba(255, 255, 255, 0.95)",
              textTransform: "uppercase",
              letterSpacing: "0.15em",
              transition: 'opacity 0.3s ease',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {insight.category_name || 'Insights'}
            </p>
          </div>
        </div>
      </div>

      {/* Hover description overlay */}
      <div 
        className="card-overlay"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.95))',
          padding: '24px 20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          textAlign: 'center',
          opacity: 0,
          transition: 'opacity 0.4s ease',
          zIndex: 2,
          boxSizing: 'border-box'
        }}
      >
        {/* Removed duplicate tags - they're already shown in the main view */}

        {/* Middle Section - Title and Description */}
        <div 
          className="card-description"
          style={{
            transform: 'translateY(20px)',
            opacity: 0,
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.1s',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            flex: 1,
            justifyContent: 'center',
            padding: '0 10px'
          }}
        >
          {/* Title */}
          <h3 style={{
            margin: "0px",
            fontFamily: "Inter, sans-serif",
            fontSize: "clamp(16px, 2.5vw, 20px)",
            fontWeight: 700,
            lineHeight: "1.3em",
            color: "white",
            letterSpacing: "-0.02em",
            textAlign: 'center',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
            maxWidth: '100%'
          }}>
            {getTitle()}
          </h3>
          
        </div>

        {/* Bottom Section - CTA Button */}
        <div style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px'
        }}>
          <div style={{
            padding: "12px 24px",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            borderRadius: "30px",
            fontSize: "10px",
            fontWeight: 700,
            color: "white",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            display: 'inline-block',
            border: '2px solid rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            minWidth: '140px',
            textAlign: 'center'
          }}>
            Click to Read More
          </div>
        </div>
      </div>
    </article>
  );
};

export default InsightCard;




